import React, { useState, useCallback, useMemo } from "react";
import { Button, Modal, Typography, Input, List } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { COLORS } from "../../../../utils/constants";
import { OpportunityRecord, OpportunityListItem } from ".";

const { Text } = Typography;

interface ImportOpportunityProps {
  opportunities: OpportunityRecord[];
  selectedOpportunity: OpportunityRecord | null;
  onOpportunitySelect: (opportunity: OpportunityRecord | null) => void;
  onImportConfirm: (opportunity: OpportunityRecord) => void;
}

const ImportOpportunity: React.FC<ImportOpportunityProps> = ({
  opportunities = [],
  selectedOpportunity,
  onOpportunitySelect,
  onImportConfirm,
}) => {
  const [projectSelectModalVisible, setProjectSelectModalVisible] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleOpenProjectSelect = () => {
    setProjectSelectModalVisible(true);
  };

  const handleCancelProjectSelect = () => {
    setProjectSelectModalVisible(false);
    onOpportunitySelect(null);
    setSearchQuery("");
  };

  const handleLoadProjectFromList = () => {
    if (selectedOpportunity) {
      onImportConfirm(selectedOpportunity);
      setProjectSelectModalVisible(false);
      onOpportunitySelect(null);
      setSearchQuery("");
    }
  };

  // Memoized callback for selecting project
  const handleSelectProject = useCallback(
    (tenderId: string) => {
      const opportunity = opportunities.find((opp) => opp.tenderId === tenderId);
      onOpportunitySelect(opportunity || null);
    },
    [opportunities, onOpportunitySelect]
  );

  // Memoized render function for list items
  const renderOpportunityItem = useCallback(
    (project: OpportunityRecord) => {
      return (
        <OpportunityListItem
          key={project.tenderId}
          project={project}
          isSelected={selectedOpportunity?.tenderId === project.tenderId}
          onSelect={handleSelectProject}
        />
      );
    },
    [selectedOpportunity, handleSelectProject]
  );

  // Filter opportunities based on search query (includes opp no) - only when modal is open
  const filteredOpportunities = useMemo(() => {
    if (!projectSelectModalVisible) {
      return [];
    }

    let filtered = opportunities;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((opp) => {
        return (
          opp.no?.toLowerCase().includes(query) ||
          opp.tender?.toLowerCase().includes(query) ||
          opp.client?.toLowerCase().includes(query) ||
          opp.siteAddress?.toLowerCase().includes(query)
        );
      });
    }

    return filtered;
  }, [opportunities, searchQuery, projectSelectModalVisible]);

  return (
    <>
      <div className="mb-6">
        <Text
          className="block mb-3"
          style={{ color: "#000", fontSize: "14px" }}
        >
          Import an existing opportunity.
        </Text>
        <div className="flex gap-3">
          <Button
            onClick={handleOpenProjectSelect}
            style={{
              backgroundColor: COLORS.primaryLight,
              borderColor: COLORS.primaryLight,
              color: COLORS.primary,
              fontWeight: 500,
            }}
          >
            Import Existing Opportunity
          </Button>
        </div>
      </div>

      {/* Project Selection Modal */}
      <Modal
        open={projectSelectModalVisible}
        onCancel={handleCancelProjectSelect}
        width={800}
        destroyOnClose
        title={
          <Typography.Title level={4} className="text-blue-900 mb-0">
            Import Client Details
          </Typography.Title>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={handleCancelProjectSelect}
              className="text-gray-600 hover:text-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLoadProjectFromList}
              type="primary"
              disabled={!selectedOpportunity}
              className="bg-blue-900 hover:bg-blue-800 border-blue-900"
            >
              Import Client Details
            </Button>
          </div>
        }
      >
        <div className="py-4">
          <div>
            {opportunities.length > 0 && (
              <>
                <Text className="mb-3 text-gray-600 block">
                  Select an opportunity to load and continue working on it:
                </Text>
                <Input
                  placeholder="Search by opportunity no., project name, client, or site address..."
                  prefix={<SearchOutlined className="text-gray-400" />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                  className="mb-4"
                  size="large"
                />
              </>
            )}

            <div className="border border-gray-200 rounded-lg max-h-96 overflow-auto">
              <List
                dataSource={filteredOpportunities}
                renderItem={renderOpportunityItem}
                pagination={
                  filteredOpportunities.length > 10
                    ? {
                        pageSize: 10,
                        showSizeChanger: false,
                        showTotal: (total) => `Total ${total} opportunities`,
                        size: "small",
                      }
                    : false
                }
                locale={{
                  emptyText: searchQuery
                    ? `No opportunities found matching "${searchQuery}"`
                    : "No opportunities available",
                }}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ImportOpportunity;
